import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { SmartLink } from "../SmartLink";
import axios from "axios";
import useBrand from "../../hooks/useBrand";
import { FREE_CALLS, EXCLUSIVE_CALLS } from "../../data/content";
import { SponsorBanner } from "../SponsorBanner";
import HOME_FAQ from "../../data/homeFaq";
import { ScrollArrows } from "./HScroll";
import { ConciergeBell, Smartphone, Calculator, ClipboardList, BarChart3, BookOpen, GraduationCap, Database, KeyRound, Crown, Star, Video, QrCode, FileText, ShieldAlert, Newspaper, Tag, ArrowRight, Play, Pause, ListMusic } from "lucide-react";

const API = `${window.__BACKEND__}/api`;
const BRIC = "'Bricolage Grotesque', system-ui, sans-serif";

// Continuous, smooth auto-scroll for image sliders (pauses on hover / touch /
// when the tab is hidden). Keeps manual scrolling fully usable.
function useAutoScroll(ref, pxPerFrame = 0.6, dep) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let raf;
    let paused = false;
    const pause = () => { paused = true; };
    const resume = () => { paused = false; };
    el.addEventListener("mouseenter", pause);
    el.addEventListener("mouseleave", resume);
    el.addEventListener("touchstart", pause, { passive: true });
    el.addEventListener("touchend", resume, { passive: true });
    const step = () => {
      if (!paused && !document.hidden && el.scrollWidth > el.clientWidth + 4) {
        if (el.scrollLeft + el.clientWidth >= el.scrollWidth - 1) el.scrollLeft = 0;
        else el.scrollLeft += pxPerFrame;
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => {
      cancelAnimationFrame(raf);
      el.removeEventListener("mouseenter", pause);
      el.removeEventListener("mouseleave", resume);
      el.removeEventListener("touchstart", pause);
      el.removeEventListener("touchend", resume);
    };
  }, [ref, dep]);
}

// ─────────────────────────── shared bits ───────────────────────────
function Eyebrow({ children }) {
  return (
    <p className="m-0 mb-1.5 text-[13px] font-extrabold uppercase" style={{ letterSpacing: "0.08em", color: "#C2410C" }}>
      {children}
    </p>
  );
}
function H2({ children, size = 34 }) {
  return (
    <h2 className="m-0" style={{ fontFamily: BRIC, fontWeight: 800, fontSize: size, letterSpacing: "-0.02em", color: "#111318", lineHeight: 1.1 }}>
      {children}
    </h2>
  );
}

// ─────────────────────────── 1. SERVIZI 3x3 ───────────────────────────
const ICON = {
  logo: "https://customer-assets.emergentagent.com/job_vita-portal/artifacts/gs2v6oy2_logo%20per%20servizi.png",
  cantiere: "https://customer-assets-m6fa6gv7.emergentagent.net/job_vita-portal/artifacts/3ue9u75z_icona%20cantiere%20host.png",
  hostlab: "https://customer-assets.emergentagent.com/job_429979d6-d25e-4b1c-9d81-45205e323c1e/artifacts/ms1xlg4l_hostlab%20logo%20nuovo%201.png",
  avvia: "https://customer-assets.emergentagent.com/job_429979d6-d25e-4b1c-9d81-45205e323c1e/artifacts/7hmjf3q6_corso%20affiancmaneto.png",
  hostland: "https://customer-assets.emergentagent.com/job_vita-portal/artifacts/m21mot33_HOSTLAND%20LOGO%20NUOVO.png",
  lt: "https://customer-assets.emergentagent.com/job_vita-portal/artifacts/v0oi7gxq_logo%20locazione%20t.png",
  cin: "https://customer-assets.emergentagent.com/job_vita-portal/artifacts/4oto69gp_cinfacilenuovo.png",
  hostclub: "https://customer-assets.emergentagent.com/job_429979d6-d25e-4b1c-9d81-45205e323c1e/artifacts/qz4yavji_HOSTCLUB%20QUADRATO.png",
  hostmarket: "https://customer-assets.emergentagent.com/job_vita-portal/artifacts/lcf6dk52_hostmarket%20nuovooo.png",
};
const SERVICES = [
  { to: "/consulenze", icon: ICON.logo, tag: "da 0€", title: "Consulenze 1:1", desc: "Con Sergio, Francesca e Lorenzo, online su Meet." },
  { to: "/cantierehost", icon: ICON.cantiere, tag: "Nuovo", title: "Cantiere Host", desc: "Ti accompagniamo nell'apertura della struttura, passo per passo." },
  { to: "/hostlab", icon: ICON.hostlab, tag: "da 10€", title: "HostLab", desc: "Lo spazio di formazione in diretta e registrato." },
  { to: "/servizi/avviabnb", icon: ICON.avvia, tag: "399€", title: "AvviaBnB", desc: "Percorso 1:1 di 10 ore per aprire la prima struttura." },
  { to: "/hostland", icon: ICON.hostland, tag: "30 gg gratis", title: "Hostland", desc: "Guestbook digitale, sito e prenotazioni dirette." },
  { to: "/servizi/lt", icon: ICON.lt, tag: "Prova gratis", title: "LocazioneTuristica.it", desc: "Contratti e gestione per host privati." },
  { to: "/servizi/cin-facile", icon: ICON.cin, tag: "Chiavi in mano", title: "CIN Facile", desc: "Ottieni il Codice Identificativo Nazionale senza impazzire." },
  { to: "/hostclub", icon: ICON.hostclub, tag: "19,90€/anno", title: "HostClub", desc: "Community premium: consulenze scontate e file gratis." },
  { to: "/servizi/hostmarket", icon: ICON.hostmarket, tag: "18 categorie", title: "HostMarket", desc: "Trova home stager, fotografi, commercialisti e altri pro." },
];

function Servizi() {
  return (
    <section id="servizi" className="flex flex-col gap-5" data-testid="home-servizi">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <Eyebrow>Servizi</Eyebrow>
          <H2 size={36}>Servizi e consulenze per host e property manager</H2>
        </div>
        <Link to="/servizi" className="font-bold underline underline-offset-4" style={{ color: "#111318" }}>Tutti i servizi →</Link>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3.5">
        {SERVICES.map((s) => (
          <SmartLink key={s.title} to={s.to} data-testid={`servizio-${s.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
            className="flex flex-col gap-3 rounded-[18px] p-5 transition-shadow hover:shadow-[0_12px_30px_-18px_rgba(0,0,0,0.3)]"
            style={{ background: "#FFFFFF", border: "1px solid #E4E6EA", minHeight: 148 }}>
            <span className="flex items-start justify-between gap-2">
              <img src={s.icon} alt="" loading="lazy" className="w-11 h-11 sm:w-12 sm:h-12 rounded-[12px] object-cover flex-none" />
              <span className="text-[11px] sm:text-[12px] font-extrabold px-2.5 py-1 rounded-full text-right leading-tight flex-none max-w-[52%]" style={{ background: "#F1F2F4", color: "#3D434E" }}>{s.tag}</span>
            </span>
            <strong className="text-[16px] sm:text-[18px] leading-tight break-words" style={{ fontFamily: BRIC, color: "#111318", overflowWrap: "anywhere" }}>{s.title}</strong>
            <span className="text-[14px] sm:text-[15px]" style={{ lineHeight: 1.45, color: "#4A505B" }}>{s.desc}</span>
          </SmartLink>
        ))}
      </div>
    </section>
  );
}

// ─────────────────────────── 2. SPONSOR (full width) ───────────────────────────
function SponsorFull() {
  return (
    <section aria-label="Sponsor" className="flex flex-col gap-2 -mt-6 -mb-6 md:-mt-8 md:-mb-8" data-testid="home-sponsor-main">
      <span className="text-[11px] font-extrabold uppercase" style={{ color: "#5B616C", letterSpacing: "0.08em" }}>Sponsor</span>
      <SponsorBanner placement="home_main" />
    </section>
  );
}

// ─────────────────────────── 3. CONSULENZE ───────────────────────────
function Consulenze() {
  const experts = [
    { ...FREE_CALLS[0], sub: "Fondatore di Vita da Host e Hostland, autore di FaqBnb", topics: ["Come avviare una nuova struttura", "Dubbi su novità e normative", "Pianificare una collaborazione"] },
    { ...FREE_CALLS[1], sub: "Home stager, responsabile HostLab", topics: ["Migliorare l'aspetto degli ambienti", "Interventi che alzano il valore percepito", "Cosa funziona e cosa evitare"] },
    { ...FREE_CALLS[2], sub: "Commercialista esperto di affitti brevi", topics: ["Quando serve la Partita IVA", "Cedolare secca e regime fiscale", "Tassazione 2026"] },
  ];
  const prices = [
    EXCLUSIVE_CALLS.find((c) => c.key === "airbnb-annuncio"),
    EXCLUSIVE_CALLS.find((c) => c.key === "meeting-1h"),
    EXCLUSIVE_CALLS.find((c) => c.key === "prezzo-dinamico"),
    EXCLUSIVE_CALLS.find((c) => c.key === "self-checkin"),
  ].filter(Boolean);
  const priceCards = [
    { title: "Annuncio Airbnb, Booking o Vrbo", dur: "2 ore", desc: "Annuncio ottimizzato parola per parola", price: 70, soci: 40, theme: "blue" },
    { title: "Meeting di approfondimento", dur: "1 ora", desc: "Analisi e strategia su misura", price: 40, soci: 20, theme: "orange" },
    { title: "Prezzo dinamico", dur: "1 ora", desc: "Tariffe per stagione e domanda", price: 40, soci: 20, theme: "red" },
    { title: "Sistema di self check-in", dur: "1 ora", desc: "Accessi autonomi senza stress", price: 40, soci: 20, theme: "grey" },
    { title: "Call potenziamento annuncio", dur: "2 ore", desc: "Foto, stile, prezzi e annuncio", price: 90, soci: null, theme: "green", href: "https://calendly.com/vitadahost/superottimizzazione" },
  ];
  const CARD_THEME = {
    blue:   { bg: "#1E4FD8", border: "transparent", text: "#FFFFFF", muted: "rgba(255,255,255,0.82)", soci: "rgba(255,255,255,0.95)" },
    orange: { bg: "#EA580C", border: "transparent", text: "#FFFFFF", muted: "rgba(255,255,255,0.82)", soci: "rgba(255,255,255,0.95)" },
    red:    { bg: "#DC2626", border: "transparent", text: "#FFFFFF", muted: "rgba(255,255,255,0.82)", soci: "rgba(255,255,255,0.95)" },
    grey:   { bg: "#64748B", border: "transparent", text: "#FFFFFF", muted: "rgba(255,255,255,0.82)", soci: "rgba(255,255,255,0.95)" },
    green:  { bg: "#0B7A4B", border: "transparent", text: "#FFFFFF", muted: "rgba(255,255,255,0.82)", soci: "rgba(255,255,255,0.95)" },
  };
  return (
    <section id="consulenze" className="flex flex-col gap-6 rounded-[24px] p-6 md:p-8" style={{ background: "#FFFFFF", border: "1px solid #E4E6EA" }} data-testid="home-consulenze">
      <div className="flex justify-between items-end gap-4 flex-wrap">
        <div>
          <Eyebrow>Consulenze</Eyebrow>
          <H2 size={34}>Consulenze gratuite di 15 minuti con i nostri esperti</H2>
        </div>
        <span className="text-[15px]" style={{ color: "#4A505B" }}>Online su Google Meet · pagamento sicuro sul sito</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3.5">
        {experts.map((e) => (
          <div key={e.key} className="flex flex-col gap-3.5 rounded-[18px] p-5" style={{ border: "1px solid #E4E6EA" }}>
            <div className="flex gap-3.5 items-center">
              <img src={e.photo} alt={`${e.name}, ${e.sub}`} width={80} height={80} loading="lazy" className="w-20 h-20 rounded-2xl object-cover flex-none" style={{ background: "#E9EBEF" }} />
              <div className="flex flex-col gap-1 min-w-0">
                <strong className="text-[18px] leading-[1.1]" style={{ color: "#111318" }}>
                  {(() => { const [first, ...rest] = e.name.split(" "); return (<>
                    <span className="block">{first}</span>
                    {rest.length > 0 && <span className="block">{rest.join(" ")}</span>}
                  </>); })()}
                </strong>
                <span className="text-[13px]" style={{ color: "#4A505B", lineHeight: 1.35 }}>{e.sub}</span>
              </div>
            </div>
            <ul className="m-0 pl-[18px] text-[14px]" style={{ lineHeight: 1.6, color: "#3D434E" }}>
              {e.topics.map((t) => <li key={t}>{t}</li>)}
            </ul>
            <a href={e.bookUrl} target="_blank" rel="noopener noreferrer" data-testid={`consulenza-free-${e.key}`}
              className="mt-auto h-12 rounded-[12px] flex items-center justify-center font-extrabold" style={{ background: "#0B7A4B", color: "#FFFFFF" }}>
              Prenota gratis · 15 min
            </a>
          </div>
        ))}
      </div>
      <div className="flex flex-col gap-2.5">
        <h3 className="m-0 text-[18px]" style={{ color: "#111318" }}>Consulenze approfondite</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2.5">
          {priceCards.map((p) => {
            const t = CARD_THEME[p.theme] || CARD_THEME.white;
            const inner = (
              <>
                <div className="flex items-baseline gap-2">
                  <span style={{ fontFamily: BRIC, fontWeight: 800, fontSize: 30, lineHeight: 1, color: t.text }}>{p.price}€</span>
                  <span className="text-[13px] font-semibold" style={{ color: t.muted }}>{p.dur}</span>
                </div>
                <span className="text-[13px] font-bold" style={{ color: t.soci }}>
                  {p.soci != null ? `Soci HostClub ${p.soci}€` : "Prenota ora"}
                </span>
                <div className="flex flex-col gap-0.5 mt-1">
                  <strong className="text-[14px] leading-[1.25]" style={{ color: t.text }}>{p.title}</strong>
                  {p.desc && <span className="text-[12px]" style={{ color: t.muted, lineHeight: 1.3 }}>{p.desc}</span>}
                </div>
              </>
            );
            const cls = "flex flex-col gap-1.5 rounded-[16px] p-4 transition-transform hover:-translate-y-0.5";
            const stl = { background: t.bg, border: `1px solid ${t.border}` };
            return p.href ? (
              <a key={p.title} href={p.href} target="_blank" rel="noopener noreferrer" className={cls} style={stl}>
                {inner}
              </a>
            ) : (
              <Link key={p.title} to="/consulenze" className={cls} style={stl}>
                {inner}
              </Link>
            );
          })}
        </div>
        <Link to="/consulenze" className="self-start h-12 px-5 rounded-[12px] flex items-center font-extrabold mt-3" style={{ background: "#111318", color: "#FFFFFF" }}>
          Tutte le consulenze a pagamento →
        </Link>
      </div>
    </section>
  );
}

// ─────────────────────── 4. BLOG EDITORIALE + PODCAST ───────────────────────
function fmtDate(d) {
  if (!d) return "";
  try {
    return new Date(d).toLocaleDateString("it-IT", { day: "numeric", month: "short" });
  } catch { return ""; }
}
function fmtDur(sec) {
  const s = parseInt(sec || 0, 10);
  if (!s) return "";
  const m = Math.floor(s / 60), r = s % 60;
  return `${m}:${String(r).padStart(2, "0")}`;
}
const CARD_BG = ["#D9DEE6", "#E3DCCF", "#D6E4DC", "#DCE0EE", "#EADCD6", "#E6E1D3"];

function useBlogFeed(limit) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    let alive = true;
    fetch(`${API}/blog-news/feed?limit=${limit}`, { cache: "no-store" })
      .then((r) => r.json())
      .then((j) => { if (alive) setItems((j.items || []).slice(0, limit)); })
      .catch(() => {});
    return () => { alive = false; };
  }, [limit]);
  return items;
}

const postHref = (p) => (p?.is_internal ? `/blog/${p.slug}` : p?.link || "#");
const postProps = (p) => (p?.is_internal ? { to: postHref(p) } : { href: postHref(p), target: "_blank", rel: "noopener noreferrer" });

// ── Barra podcast "Host & Furious": play ultimo episodio + lista episodi ──
function PodcastBar() {
  const [data, setData] = useState(null);
  const [playingId, setPlayingId] = useState(null);
  const audioRef = useRef(null);
  useEffect(() => {
    let alive = true;
    fetch(`${API}/podcast/feed?limit=20`, { cache: "no-store" })
      .then((r) => r.json())
      .then((j) => { if (alive) setData(j); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  if (!data || !(data.episodes && data.episodes.length)) return null;
  const show = data.show || {};
  const eps = data.episodes;
  const latest = eps[0];
  const play = (ep) => {
    const a = audioRef.current;
    if (!a) return;
    if (playingId === ep.id) { a.pause(); setPlayingId(null); return; }
    a.src = ep.audio_url; a.play().catch(() => {}); setPlayingId(ep.id);
  };
  const WAVE = [0.9, 1.3, 0.7, 1.15, 1.5, 0.85, 1.25, 0.95, 1.4, 0.75, 1.1, 1.35, 0.8, 1.2];
  const Wave = ({ playing }) => (
    <span className="inline-flex items-center gap-[2px] h-[16px] flex-none" aria-hidden="true" data-testid="podcast-wave">
      {WAVE.map((d, i) => (
        <span key={i} style={{
          width: 2.5, height: 4, borderRadius: 2, background: "#F4C84A",
          animation: `vdhWave ${(playing ? d * 0.7 : d).toFixed(2)}s ${((i % 6) * 0.08).toFixed(2)}s ease-in-out infinite`,
        }} />
      ))}
    </span>
  );
  return (
    <div data-testid="home-podcast-bar">
      <style dangerouslySetInnerHTML={{ __html: "@keyframes vdhWave{0%,100%{height:4px;opacity:.5}50%{height:16px;opacity:1}}" }} />
      <audio ref={audioRef} onEnded={() => setPlayingId(null)} preload="none" />
      <div className="flex items-center gap-3 rounded-[16px] px-3 py-2.5 md:px-4 md:py-3"
        style={{ background: "linear-gradient(100deg,#17171C 0%,#241a31 100%)", border: "1px solid #2E2E38" }}>
        {show.image && <img src={show.image} alt="" className="w-11 h-11 md:w-12 md:h-12 rounded-[10px] object-cover flex-none" />}
        <button onClick={() => play(latest)} data-testid="podcast-play-latest" aria-label="Ascolta l'ultimo episodio"
          className="flex-none w-11 h-11 rounded-full flex items-center justify-center transition-transform hover:scale-105"
          style={{ background: "#E0320F", color: "#fff", boxShadow: "0 8px 20px -8px rgba(224,50,15,.8)" }}>
          {playingId === latest.id ? <Pause size={20} fill="#fff" /> : <Play size={20} fill="#fff" />}
        </button>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2.5">
            <span className="text-[10px] font-extrabold uppercase truncate" style={{ letterSpacing: "0.09em", color: "#F4C84A" }}>Podcast · {show.title || "Host & Furious"}</span>
            <Wave playing={!!playingId} />
          </div>
          <div className="truncate text-[14px] md:text-[15px] font-bold text-white" style={{ fontFamily: BRIC }}>{latest.title}</div>
        </div>
        <Link to="/podcast" data-testid="podcast-episodes-toggle"
          className="flex-none h-10 px-3 md:px-4 rounded-full flex items-center gap-2 text-[13px] font-bold transition-colors hover:bg-white/10"
          style={{ border: "1px solid rgba(255,255,255,.28)", color: "#fff" }}>
          <ListMusic size={16} /> <span className="hidden sm:inline">Episodi</span>
        </Link>
      </div>
    </div>
  );
}

// ── Post in evidenza (ultimo): banner immagine ampio + titolo + prime 5 righe ──
function FeaturedPost({ p }) {
  const Comp = p.is_internal ? Link : "a";
  return (
    <Comp {...postProps(p)} data-testid="blog-featured" className="group block rounded-[22px] overflow-hidden transition-shadow hover:shadow-[0_18px_44px_-24px_rgba(0,0,0,0.4)]"
      style={{ background: "#FFFFFF", border: "1px solid #E4E6EA" }}>
      <div className="relative w-full" style={{ aspectRatio: "16 / 8", background: "#D9DEE6" }}>
        {p.image && <img src={p.image} alt={p.image_alt || p.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" />}
      </div>
      <div className="p-5 md:p-7 flex flex-col gap-3">
        <span className="text-[12px] font-extrabold uppercase" style={{ letterSpacing: "0.08em", color: "#C2410C" }}>In evidenza</span>
        <h3 className="m-0 text-[24px] md:text-[32px]" style={{ fontFamily: BRIC, fontWeight: 800, lineHeight: 1.12, letterSpacing: "-0.02em", color: "#111318" }}>{p.title}</h3>
        {p.excerpt && (
          <p className="m-0 text-[15px] md:text-[17px]" style={{ color: "#4A505B", lineHeight: 1.6, display: "-webkit-box", WebkitLineClamp: 5, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{p.excerpt}</p>
        )}
        <span className="text-[13px]" style={{ color: "#5B616C" }}>{fmtDate(p.published)}{p.author ? ` · ${p.author}` : ""}{p.reading_minutes ? ` · ${p.reading_minutes} min` : ""}</span>
        <span className="inline-flex items-center gap-1.5 font-bold mt-1" style={{ color: "#111318" }}>Leggi l'articolo <ArrowRight size={17} className="transition-transform group-hover:translate-x-1" /></span>
      </div>
    </Comp>
  );
}

// ── Gli altri articoli (ultimi 10): card stile blog, 2 per riga, con estratto ──
function BlogMore({ items }) {
  return (
    <section className="flex flex-col gap-5" data-testid="home-blog-more">
      <H2 size={24}>Altri articoli</H2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
        {items.map((p, i) => {
          const Comp = p.is_internal ? Link : "a";
          return (
            <Comp key={p.slug || i} {...postProps(p)} data-testid={`blog-more-${i}`}
              className="group flex flex-col rounded-[18px] overflow-hidden transition-shadow hover:shadow-[0_18px_40px_-24px_rgba(0,0,0,0.4)]"
              style={{ background: "#FFFFFF", border: "1px solid #E4E6EA" }}>
              <div className="relative w-full" style={{ aspectRatio: "16 / 9", background: CARD_BG[i % CARD_BG.length] }}>
                {p.image && <img src={p.image} alt={p.image_alt || p.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]" />}
              </div>
              <div className="p-5 flex flex-col gap-2.5 flex-1">
                <span className="text-[12px]" style={{ color: "#5B616C" }}>{fmtDate(p.published)}{p.author ? ` · ${p.author}` : ""}{p.reading_minutes ? ` · ${p.reading_minutes} min` : ""}</span>
                <strong className="text-[19px] md:text-[20px]" style={{ fontFamily: BRIC, fontWeight: 800, lineHeight: 1.22, letterSpacing: "-0.01em", color: "#111318", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{p.title}</strong>
                {p.excerpt && (
                  <p className="m-0 text-[14.5px]" style={{ color: "#4A505B", lineHeight: 1.55, display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{p.excerpt}</p>
                )}
                <span className="inline-flex items-center gap-1.5 font-bold text-[14px] mt-auto pt-1" style={{ color: "#C2410C" }}>
                  Continua a leggere <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                </span>
              </div>
            </Comp>
          );
        })}
      </div>
      <Link to="/blog" data-testid="blog-more-all" className="self-start mt-1 h-11 px-5 rounded-[12px] flex items-center font-bold" style={{ border: "1px solid #111318", color: "#111318" }}>Tutti gli articoli →</Link>
    </section>
  );
}

// ─────────────────────────── 5. COS'È ───────────────────────────
function CosE() {
  const brand = useBrand();
  return (
    <section className="flex flex-wrap gap-6 rounded-[24px] p-6 md:p-8" style={{ background: "#FFFFFF", border: "1px solid #E4E6EA" }} data-testid="home-cose">
      <div className="flex flex-col gap-3.5 min-w-0" style={{ flex: "999 1 380px" }}>
        <H2 size={32}>Cos'è Vita da Host</H2>
        <p className="m-0 text-[18px]" style={{ lineHeight: 1.6, color: "#2C313A" }}>
          Vita da Host è il portale italiano per chi gestisce B&amp;B, case vacanza, affittacamere, agriturismi, glamping e locazioni turistiche. Ogni giorno pubblica notizie e guide su normative, tasse e piattaforme come Airbnb e Booking, offre consulenze con professionisti del settore, corsi in diretta e on demand e strumenti gratuiti. È nato a Genova nel 2023 da host che gestiscono strutture in prima persona.
        </p>
      </div>
      <dl className="m-0 rounded-[18px] p-5 grid grid-cols-2 gap-x-4 gap-y-3.5 text-[14px]" style={{ flex: "1 1 260px", background: "#F6F7F9" }}>
        <div><dt style={{ color: "#5B616C" }}>Sede</dt><dd className="m-0 mt-0.5 font-bold" style={{ color: "#111318" }}>Genova</dd></div>
        <div><dt style={{ color: "#5B616C" }}>Dal</dt><dd className="m-0 mt-0.5 font-bold" style={{ color: "#111318" }}>{brand.anno_fondazione}</dd></div>
        <div><dt style={{ color: "#5B616C" }}>Fondatore</dt><dd className="m-0 mt-0.5 font-bold" style={{ color: "#111318" }}>{brand.fondatore}</dd></div>
        <div><dt style={{ color: "#5B616C" }}>P.IVA</dt><dd className="m-0 mt-0.5 font-bold" style={{ color: "#111318" }}>{brand.piva}</dd></div>
        <div className="col-span-2"><dt style={{ color: "#5B616C" }}>Per chi</dt><dd className="m-0 mt-0.5 font-bold" style={{ color: "#111318" }}>Host, PM, B&amp;B, case vacanza, affittacamere, agriturismi</dd></div>
      </dl>
    </section>
  );
}

// ─────────────────────────── 7. PERCORSI ───────────────────────────
const SERGIO_CALL_PHOTO = "https://customer-assets-m6fa6gv7.emergentagent.net/job_vita-portal/artifacts/743b45pd_foto%20sergio%20sito.png";
// pill tones
const PILL = {
  green: { bg: "#E7F5EE", color: "#0B7A4B" },
  amber: { bg: "#FBEFD2", color: "#92400E" },
  grey:  { bg: "#EEF0F3", color: "#3D434E" },
};
const IC = {
  faqbnb: "https://customer-assets.emergentagent.com/job_vita-portal/artifacts/2v62obec_NUOVA%20ICONA%20FAQBNB.png",
  corsi: "https://customer-assets-m6fa6gv7.emergentagent.net/job_vita-portal/artifacts/07aa09a46135433b_icona%20corso.png",
  calcolatore: "https://customer-assets-m6fa6gv7.emergentagent.net/job_vita-portal/artifacts/zqc087bd_calcola%20affitti.png",
  cin: "https://customer-assets-m6fa6gv7.emergentagent.net/job_vita-portal/artifacts/hwvnkam3_cin%20facilleeee.png",
  hostrevenue: "https://customer-assets-m6fa6gv7.emergentagent.net/job_vita-portal/artifacts/db7s8frg_hrevenue%20.png",
  hostland: "https://customer-assets.emergentagent.com/job_vita-portal/artifacts/1v483id5_HOSTLAND%20LOGO%20NUOVO.png",
  hostlab: "https://customer-assets-m6fa6gv7.emergentagent.net/job_vita-portal/artifacts/y2wyrhaj_hostlab%20logo%20nuovo%201.webp",
  calculhost: "https://customer-assets.emergentagent.com/job_vita-portal/artifacts/7uyo79ju_impost%20soggio.png",
  hostclub: "https://customer-assets.emergentagent.com/job_429979d6-d25e-4b1c-9d81-45205e323c1e/artifacts/qz4yavji_HOSTCLUB%20QUADRATO.png",
  scelto: "https://customer-assets.emergentagent.com/job_vita-portal/artifacts/cahdbxsp_nuova%20scelto.png",
  devisu: "https://customer-assets.emergentagent.com/job_vita-portal/artifacts/hyuddqbb_DEVISU.png",
  cody: "https://customer-assets.emergentagent.com/job_vita-portal/artifacts/ooxdyuh0_cody%20nuovo.png",
  cantiere: "https://customer-assets-m6fa6gv7.emergentagent.net/job_vita-portal/artifacts/4rzaodik_icona%20cantiere%20host.png",
  selfcheckin: "https://customer-assets-m6fa6gv7.emergentagent.net/job_vita-portal/artifacts/o18a6ema_self%20check%20in%20legale.png",
  blog: "https://customer-assets-m6fa6gv7.emergentagent.net/job_vita-portal/artifacts/89ujsmdg_blog%20nuovo.png",
};
const PERCORSI = [
  {
    n: "01", count: "6 strumenti",
    headBg: "#FFD23F", headText: "#111318", headSub: "rgba(17,19,24,0.64)", numText: "#B45309", iconBg: "#FBEFD2", iconColor: "#B45309", accent: "#B45309",
    title: "Vuoi aprire una struttura",
    subtitle: "Dall'idea al primo ospite, senza errori.",
    rows: [
      { img: IC.cantiere, title: "Cantiere Host", sub: "Ti accompagniamo fino all'apertura", pill: "Nuovo", tone: "amber", to: "/cantierehost" },
      { photo: SERGIO_CALL_PHOTO, title: "Chiamata gratuita con Sergio", sub: "15 minuti per partire col piede giusto", pill: "Gratis", tone: "green", to: "https://vitadahost.short.gy/Call15Min", external: true },
      { img: IC.corsi, title: "I Nostri Corsi", sub: "Diventa Host, PM o HomeStager", pill: "Nuovo", tone: "amber", to: "/corsi" },
      { img: IC.calcolatore, title: "QuantoGuadagni?", sub: "Affitto breve o tradizionale", pill: "Gratis", tone: "green", to: "/calcolatoreguadagniaffittibrevi" },
      { img: IC.cin, title: "CIN Facile", sub: "La pratica chiavi in mano", pill: "Servizio", tone: "amber", to: "/servizi/cin-facile" },
      { img: IC.hostrevenue, title: "HostRevenue", sub: "Analisi di mercato con dati reali", pill: "19,90€", tone: "amber", to: "/servizi/hostrevenue" },
    ],
  },
  {
    n: "02", count: "6 strumenti",
    headBg: "#15A34A", headText: "#FFFFFF", headSub: "rgba(255,255,255,0.88)", numText: "#0B7A4B", iconBg: "#E4F3EA", iconColor: "#0B7A4B", accent: "#0B7A4B",
    title: "Gestisci già una struttura",
    subtitle: "Regole, adempimenti e strumenti di ogni giorno.",
    rows: [
      { img: IC.hostland, title: "Hostland", sub: "Welcomebook, sito e prenotazioni dir…", pill: "30 gg gratis", tone: "green", to: "/hostland" },
      { img: IC.hostlab, title: "HostLab", sub: "Webinar live con esperti veri", pill: "10€", tone: "amber", to: "/servizi/hostlab" },
      { img: IC.calculhost, title: "CalculHost", sub: "Imposta di soggiorno dai file OTA", pill: "Gratis", tone: "green", to: "/strumenti/calcolo-imposta-soggiorno" },
      { img: "https://customer-assets-m6fa6gv7.emergentagent.net/job_vita-portal/artifacts/88chy125_cosulenze%20home.png", title: "Consulenza annunci", sub: "Airbnb, Booking e Vrbo ottimizzati", pill: "da 20€", tone: "amber", to: "/consulenze" },
      { img: IC.hostclub, title: "HostClub", sub: "Consulenze scontate e file gratis", pill: "19,90€/anno", tone: "amber", to: "/hostclub" },
      { img: IC.scelto, title: "Scelto da Vita da Host", sub: "Il bollino di qualità sulla mappa", pill: "Gratis", tone: "green", to: "/servizi/scelto-da-vita-da-host" },
    ],
  },
  {
    n: "03", count: "6 strumenti",
    headBg: "#2563EB", headText: "#FFFFFF", headSub: "rgba(255,255,255,0.90)", numText: "#1E4FD8", iconBg: "#E3ECFC", iconColor: "#1E4FD8", accent: "#1E4FD8",
    title: "Sei un property manager",
    subtitle: "Più strutture, meno lavoro manuale.",
    rows: [
      { img: IC.hostland, title: "Hostland", sub: "Welcomebook, sito e prenotazioni dir…", pill: "30 gg gratis", tone: "green", to: "/hostland" },
      { img: IC.devisu, title: "DeVisu", sub: "Check-in in videochiamata", pill: "Nuovo", tone: "amber", to: "/devisu" },
      { img: IC.hostlab, title: "HostLab", sub: "Webinar live con esperti veri", pill: "10€", tone: "amber", to: "/servizi/hostlab" },
      { img: IC.cody, title: "Cody", sub: "QR HD per wifi e link", pill: "Gratis", tone: "green", to: "/qrcodegratis" },
      { img: IC.hostclub, title: "HostClub", sub: "Consulenze scontate e file gratis", pill: "19,90€/anno", tone: "amber", to: "/hostclub" },
      { img: IC.blog, title: "Blog", sub: "Normative e notizie ogni giorno", pill: "Gratis", tone: "green", to: "/blog" },
    ],
  },
];
export function Percorsi() {
  const [tab, setTab] = useState(0);
  const TAB_LABELS = ["Aprire", "Gestire", "PM"];
  const Row = ({ r, card }) => {
    const p = PILL[r.tone] || PILL.grey;
    const inner = (
      <>
        {r.photo ? (
          <img src={r.photo} alt="" className="w-11 h-11 rounded-[12px] object-cover flex-none" style={{ background: "#111318", border: "1px solid #E4E6EA" }} />
        ) : r.img ? (
          <img src={r.img} alt={`Icona di ${r.title}`} width={44} height={44} className="w-11 h-11 rounded-[12px] object-cover flex-none" />
        ) : (
          <span className="w-11 h-11 rounded-[12px] flex items-center justify-center flex-none" style={{ background: card.iconBg, color: card.iconColor }}>
            <r.Icon size={20} strokeWidth={1.9} />
          </span>
        )}
        <span className="flex flex-col min-w-0 flex-1">
          <strong className="text-[16px] leading-tight truncate" style={{ color: "#111318" }}>{r.title}</strong>
          <span className="text-[13px] leading-tight truncate" style={{ color: "#8A909B" }}>{r.sub}</span>
        </span>
        <span className="text-[12px] font-bold px-2.5 py-1 rounded-full flex-none" style={{ background: p.bg, color: p.color }}>{r.pill}</span>
        <span className="flex-none" style={{ color: "#C0C5CE" }}>›</span>
      </>
    );
    const cls = "flex items-center gap-3 px-3.5 py-3 rounded-[14px] hover:bg-[#F6F7F9] transition-colors";
    return r.external
      ? <a href={r.to} target="_blank" rel="noopener noreferrer" className={cls}>{inner}</a>
      : <SmartLink to={r.to} className={cls}>{inner}</SmartLink>;
  };
  return (
    <section id="percorsi" className="flex flex-col gap-5 scroll-mt-28" data-testid="home-percorsi">
      <div className="flex justify-between items-end gap-4 flex-wrap">
        <div>
          <Eyebrow>Percorsi</Eyebrow>
          <H2 size={34}>Da dove vuoi partire: aprire, gestire o crescere come property manager</H2>
        </div>
        <Link to="/servizi" className="vdh-btn-yellow h-11 px-4 rounded-[12px] flex items-center font-extrabold">Tutti i servizi →</Link>
      </div>
      <div className="md:hidden flex gap-2" data-testid="percorsi-tabs">
        {TAB_LABELS.map((l, i) => {
          const c = PERCORSI[i];
          const active = tab === i;
          return (
            <button key={l} type="button" onClick={() => setTab(i)} data-testid={`percorsi-tab-${i}`}
              className="flex-1 h-11 rounded-full font-extrabold text-[14px] transition-all"
              style={{
                background: c.headBg,
                color: c.headText,
                opacity: active ? 1 : 0.5,
                boxShadow: active ? "0 8px 18px -8px rgba(0,0,0,0.45)" : "none",
                transform: active ? "translateY(-1px)" : "none",
              }}>
              {l}
            </button>
          );
        })}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {PERCORSI.map((card, idx) => (
          <div key={card.n} className={`${idx === tab ? "flex" : "hidden"} md:flex rounded-[24px] overflow-hidden flex-col`} style={{ background: "#FFFFFF", border: "1px solid #E9EBEF" }} data-testid={`percorso-${card.n}`}>
            <div className="px-5 pt-5 pb-4" style={{ background: card.headBg }}>
              <div className="flex items-center justify-between mb-3">
                <span className="text-[13px] font-extrabold px-2.5 py-1 rounded-full" style={{ background: "#FFFFFF", color: card.numText }}>{card.n}</span>
                <span className="text-[13px] font-bold" style={{ color: card.headText }}>{card.count}</span>
              </div>
              <h3 className="m-0 text-[24px] leading-tight" style={{ fontFamily: BRIC, color: card.headText }}>{card.title}</h3>
              <p className="m-0 mt-1.5 text-[14px]" style={{ color: card.headSub }}>{card.subtitle}</p>
            </div>
            <div className="p-2.5 flex flex-col gap-0.5">
              {card.rows.map((r) => <Row key={r.title} r={r} card={card} />)}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ─────────────────────────── 8. GUIDE NORMATIVE ───────────────────────────
// Icon tiles follow the mockup colour coding (tint bg + stroke colour).
const GuideIcon = ({ kind }) => {
  const map = {
    fisco:   { bg: "#E8F6EE", stroke: "#0B5E3A", path: <><path d="M4 7a8 3 0 1 0 16 0a8 3 0 1 0-16 0" /><path d="M4 7v5c0 1.7 3.6 3 8 3s8-1.3 8-3V7" /><path d="M4 12v5c0 1.7 3.6 3 8 3s8-1.3 8-3v-5" /></> },
    questura:{ bg: "#E6F0FF", stroke: "#1F4FA8", path: <><path d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z" /><path d="M9 12l2 2 4-4" /></> },
    checkin: { bg: "#F1E9FF", stroke: "#5B2CA8", path: <><path d="M8 15a4 4 0 1 1 0-8a4 4 0 0 1 0 8z" /><path d="M11 12h10" /><path d="M17 12v3" /><path d="M20 12v2" /></> },
    soggiorno:{ bg: "#FFF4D6", stroke: "#8A5300", path: <><path d="M3 21h18" /><path d="M5 21V9l7-5 7 5v12" /><path d="M9 21v-6h6v6" /></> },
    scia:    { bg: "#FDECE7", stroke: "#9A3412", path: <><path d="M6 3h8l4 4v14H6z" /><path d="M14 3v4h4" /><path d="M9 13h6" /><path d="M9 17h6" /></> },
    faq:     { bg: "#E6F0FF", stroke: "#1F4FA8", path: <><path d="M12 21a9 9 0 1 1 0-18a9 9 0 0 1 0 18z" /><path d="M9.5 9.5a2.5 2.5 0 1 1 3.5 2.3c-.6.3-1 .8-1 1.5V14" /><path d="M12 17h.01" /></> },
    rai:     { bg: "#E8F6EE", stroke: "#0B5E3A", path: <><path d="M3 7h18v12H3z" /><path d="M8 3l4 4 4-4" /></> },
  };
  const c = map[kind] || map.fisco;
  return (
    <span className="flex-none flex items-center justify-center rounded-[13px]" style={{ width: 44, height: 44, background: c.bg }}>
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={c.stroke} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">{c.path}</svg>
    </span>
  );
};
const GUIDE = [
  { kind: "fisco", cat: "Fisco", catBg: "#E8F6EE", catColor: "#0B5E3A", t: "Cedolare secca e tasse", d: "21% o 26%, ritenuta delle piattaforme e quando serve la Partita IVA.", to: "/servizi/faq-bdsr" },
  { kind: "questura", cat: "Questura", catBg: "#E6F0FF", catColor: "#1F4FA8", t: "Alloggiati Web", d: "Come comunicare gli ospiti alla Questura e cosa fare se il portale non va.", to: "/servizi/alloggiati-web" },
  { kind: "checkin", cat: "Check-in", catBg: "#F1E9FF", catColor: "#5B2CA8", t: "Self check-in", d: "Identificazione degli ospiti e strumenti in regola dopo la sentenza 2025.", to: "/servizi/self-checkin" },
  { kind: "soggiorno", cat: "Comune", catBg: "#FFF4D6", catColor: "#8A5300", t: "Imposta di soggiorno", d: "Tariffe dei Comuni, esenzioni e dichiarazione annuale.", to: "/servizi/dichiarazione-imposta-soggiorno" },
  { kind: "scia", cat: "Regione", catBg: "#FDECE7", catColor: "#9A3412", t: "SCIA e CIR", d: "Quando servono e cosa cambia da Regione a Regione.", to: "/servizi/faq-bdsr" },
  { kind: "faq", cat: "Ministero", catBg: "#E6F0FF", catColor: "#1F4FA8", t: "FAQ ufficiali BDSR", d: "Le risposte del Ministero del Turismo, spiegate semplici.", to: "/servizi/faq-bdsr" },
  { kind: "rai", cat: "Canone", catBg: "#E8F6EE", catColor: "#0B5E3A", t: "Canone RAI", d: "Quando si paga per la casa vacanze e come comportarsi.", to: "/servizi/faq-bdsr" },
];
const GuideArrow = ({ dark }) => (
  <span className="flex-none flex items-center justify-center rounded-full" style={{ width: 34, height: 34, background: dark ? "#1E2129" : "#F1F2F4" }}>
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={dark ? "#FFFFFF" : "#111318"} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
  </span>
);
function Guide() {
  return (
    <section className="flex flex-col gap-5" data-testid="home-guide">
      <div className="flex justify-between items-end gap-4 flex-wrap">
        <div>
          <Eyebrow>Guide normative</Eyebrow>
          <H2 size={34}>Le regole degli affitti brevi, spiegate semplici</H2>
        </div>
        <Link to="/servizi" className="font-extrabold text-[14px] flex items-center gap-1.5 h-11" style={{ color: "#111318" }}>Tutte le guide →</Link>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3.5">
        {/* CIN — featured dark card, spans 2 columns on desktop */}
        <Link to="/servizi/cin" className="md:col-span-2 flex flex-col md:flex-row gap-5 rounded-[22px] p-5 md:p-6" style={{ background: "#111318", color: "#FFFFFF" }} data-testid="guide-cin">
          <div className="flex-1 min-w-0 flex flex-col gap-3">
            <span className="self-start inline-flex items-center gap-2 text-[12px] font-extrabold px-2.5 py-1.5 rounded-full" style={{ background: "#1E2129" }}>
              <span className="w-2 h-2 rounded-full" style={{ background: "#FFC233" }} />Obbligatorio per tutte le strutture
            </span>
            <strong style={{ fontFamily: BRIC, fontSize: 24, lineHeight: 1.1, letterSpacing: "-0.02em" }}>CIN: il codice che ogni struttura deve avere</strong>
            <span className="text-[15px]" style={{ lineHeight: 1.5, color: "#C2C7D0" }}>Cos'è il Codice Identificativo Nazionale, come si ottiene e dove va esposto.</span>
            <div className="flex flex-wrap gap-1.5 mt-0.5">
              {["Richiedilo in BDSR", "Esponi la targa", "Mettilo negli annunci"].map((s, i) => (
                <span key={i} className="text-[12px] font-bold px-2.5 py-1.5 rounded-lg" style={{ background: "#1E2129" }}><span style={{ color: "#FFC233" }}>{i + 1}</span> {s}</span>
              ))}
            </div>
            <span className="mt-auto flex items-center gap-2.5 font-extrabold text-[14px]" style={{ color: "#FFC233" }}>
              Leggi la guida <GuideArrow dark />
            </span>
          </div>
          <div className="flex-none flex items-center justify-center" style={{ width: 196, perspective: 700 }}>
            <div className="cin-plate relative overflow-hidden flex flex-col justify-center px-[18px]" style={{ width: 196, height: 124, borderRadius: 14, background: "#FFC233", color: "#111318", boxShadow: "0 18px 30px -14px rgba(255,194,51,.55)" }}>
              <span className="text-[11px] font-extrabold" style={{ letterSpacing: "0.12em" }}>CODICE IDENTIFICATIVO NAZIONALE</span>
              <span style={{ fontFamily: BRIC, fontWeight: 800, fontSize: 40, letterSpacing: "-0.02em", lineHeight: 1 }}>CIN</span>
              <span className="text-[13px] font-bold mt-1" style={{ letterSpacing: "0.18em" }}>IT · · · · · · ·</span>
            </div>
          </div>
        </Link>

        {/* 7 guide cards */}
        {GUIDE.map((g) => (
          <Link key={g.t} to={g.to} className="flex flex-col gap-2.5 rounded-[22px] p-5" style={{ background: "#FFFFFF", border: "1px solid #E4E6EA", minHeight: 196 }}>
            <div className="flex justify-between items-center">
              <GuideIcon kind={g.kind} />
              <span className="text-[11px] font-extrabold px-2 py-1 rounded-full" style={{ color: g.catColor, background: g.catBg }}>{g.cat}</span>
            </div>
            <strong style={{ fontFamily: BRIC, fontSize: 19, lineHeight: 1.15, letterSpacing: "-0.01em", color: "#111318" }}>{g.t}</strong>
            <span className="text-[14px]" style={{ lineHeight: 1.5, color: "#4A505B" }}>{g.d}</span>
            <span className="mt-auto flex items-center justify-between font-extrabold text-[14px]" style={{ color: "#111318" }}>
              Leggi la guida <GuideArrow />
            </span>
          </Link>
        ))}
      </div>
    </section>
  );
}

// ─────────────────────────── 9. STRUMENTI GRATUITI ───────────────────────────
const MINT = "#3EE08F";
const T_IC = {
  hostscan: "https://customer-assets.emergentagent.com/job_vita-portal/artifacts/p2lezexb_HOSTSCAN.png",
  wcity: "https://customer-assets.emergentagent.com/job_vita-portal/artifacts/hfkypkft_wcity.png",
  hostmarket: "https://customer-assets.emergentagent.com/job_vita-portal/artifacts/70urwifp_hostmarket%20nuovooo.png",
  incubo: "https://customer-assets.emergentagent.com/job_vita-portal/artifacts/nrxyuo8i_BNBINCUBO.png",
  radar: "https://customer-assets-m6fa6gv7.emergentagent.net/job_vita-portal/artifacts/71177286ecbd3754_radar%20app.png",
};
const TOOLS = [
  { t: "QuantoGuadagni?", d: "Netto Airbnb, Booking e diretto", to: "/calcolatoreguadagniaffittibrevi", img: IC.calcolatore },
  { t: "CalculHost", d: "Imposta di soggiorno in 2 click", to: "/strumenti/calcolo-imposta-soggiorno", img: IC.calculhost },
  { t: "Radar dei vettori", d: "Newsletter voli e traghetti", to: "/radar", img: T_IC.radar },
  { t: "HostScan", d: "Voto 0-100 al tuo annuncio", to: "/tools/analizza-annuncio", img: T_IC.hostscan },
  { t: "AntiScam", d: "Smaschera i messaggi truffa", to: "/antiscam", Icon: ShieldAlert },
  { t: "Cody", d: "QR HD per wifi e link", to: "/qrcodegratis", img: IC.cody },
  { t: "HostNews", d: "Il giornale degli affitti brevi", to: "/blog", Icon: Newspaper },
  { t: "Recensioni Booking", d: "Guida ospiti in 7 lingue", to: "/servizi/tool-recensioni-booking", Icon: Star },
  { t: "WCity", d: "Bagni pubblici + PDF con QR", to: "/tools/bagni-pubblici", img: T_IC.wcity },
  { t: "Codici sconto", d: "PriceLabs, Chekin e altri", to: "/negozio/codici-sconto", Icon: Tag },
  { t: "HostMarket", d: "Trova i professionisti", to: "/servizi/hostmarket", img: T_IC.hostmarket },
  { t: "Scelto da Vita da Host", d: "Mappa e bollino di qualità", to: "/servizi/scelto-da-vita-da-host", img: IC.scelto },
  { t: "BnB da Incubo", d: "Il contest per la svolta", to: "/servizi/scelto-da-vita-da-host", img: T_IC.incubo },
];
function Strumenti() {
  return (
    <section className="flex flex-col gap-4 rounded-[24px] p-5 md:p-8" style={{ background: "#0E1015", color: "#FFFFFF" }} data-testid="home-strumenti">
      <div className="flex flex-col gap-2">
        <span className="self-start inline-flex items-center gap-1.5 text-[11px] font-extrabold px-2.5 py-1.5 rounded-full" style={{ background: MINT, color: "#06281A", letterSpacing: "0.06em" }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#06281A" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12l5 5L20 7" /></svg>
          100% GRATIS
        </span>
        <h2 className="m-0" style={{ fontFamily: BRIC, fontWeight: 800, fontSize: 30, lineHeight: 1.05, letterSpacing: "-0.03em", color: "#FFFFFF" }}>Strumenti gratuiti per host</h2>
        <p className="m-0 text-[14px]" style={{ color: "#A9AFBA" }}>Senza registrazione, senza carta.</p>
      </div>

      {/* 2 TOP CARDS */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="rounded-[18px] p-3.5 flex flex-col gap-3" style={{ background: "#16191F", border: "1px solid #262A33" }}>
          <div className="flex items-center gap-3">
            <div aria-hidden className="flex flex-none">
              {[FREE_CALLS[0], FREE_CALLS[1], FREE_CALLS[2]].map((p, i) => (
                <img key={p.key} src={p.photo} alt={`Foto di ${p.name}`} loading="lazy" className="rounded-full object-cover" style={{ width: 38, height: 38, border: "2px solid #16191F", marginLeft: i ? -10 : 0, background: "#3A3F4B" }} />
              ))}
            </div>
            <span className="flex flex-col">
              <strong className="text-[16px]">Consulenza gratuita</strong>
              <span className="text-[13px]" style={{ color: "#A9AFBA" }}>15 minuti con un esperto</span>
            </span>
          </div>
          <Link to="/consulenze" className="vdh-btn-yellow h-[46px] rounded-[12px] flex items-center justify-center font-extrabold text-[15px]">Prenota gratis</Link>
        </div>

        <div className="rounded-[18px] p-3.5 flex gap-3 items-center" style={{ background: "#16191F", border: "1px solid #262A33" }}>
          <img src={IC.faqbnb} alt="Copertina dell'ebook FaqBnb 2026" loading="lazy" className="flex-none rounded-[12px] object-cover block" style={{ width: 72, height: 72 }} />
          <span className="flex-1 flex flex-col gap-0.5 min-w-0">
            <strong className="text-[16px]">Ebook FaqBnb 2026</strong>
            <span className="text-[13px]" style={{ color: "#A9AFBA" }}>67 pagine per non sbagliare</span>
          </span>
          <Link to="/servizi/faqbnb" className="vdh-btn-yellow h-[44px] px-3.5 rounded-[12px] flex items-center font-extrabold text-[14px]">Scarica</Link>
        </div>
      </div>

      {/* 12 TOOLS GRID — compact rows (icon beside text) */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2.5">
        {TOOLS.map((tool) => (
          <Link key={tool.t} to={tool.to} className="flex items-center gap-2.5 rounded-[14px] p-2.5" style={{ background: "#16191F", border: "1px solid #262A33", color: "#FFFFFF" }}>
            {tool.img ? (
              <img src={tool.img} alt="" loading="lazy" className="rounded-[9px] object-cover block flex-none" style={{ width: 38, height: 38 }} />
            ) : (
              <span className="flex items-center justify-center rounded-[9px] flex-none" style={{ width: 38, height: 38, background: "#23272F" }}>
                <tool.Icon size={19} style={{ color: MINT }} strokeWidth={2} />
              </span>
            )}
            <span className="flex flex-col min-w-0">
              <strong className="text-[13.5px] truncate" style={{ lineHeight: 1.2 }}>{tool.t}</strong>
              <span className="text-[11.5px]" style={{ lineHeight: 1.3, color: "#A9AFBA" }}>{tool.d}</span>
            </span>
          </Link>
        ))}
      </div>

      <Link to="/servizi" className="vdh-btn-yellow h-[48px] rounded-[12px] flex items-center justify-center font-extrabold text-[15px]">Vedi tutti gli strumenti</Link>
    </section>
  );
}

// ─────────────────────────── 10. HOSTMARKET SLIDER ───────────────────────────
function HostMarketSlider() {
  const [items, setItems] = useState([]);
  const scroller = useRef(null);
  useAutoScroll(scroller, 0.5, items.length);
  useEffect(() => {
    let alive = true;
    fetch(`${API}/hostmarket/feed?limit=25`).then((r) => r.json())
      .then((j) => { if (alive) setItems((j.items || []).slice(0, 15)); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  if (!items.length) return null;
  return (
    <section className="flex flex-col gap-4" data-testid="home-hostmarket-slider">
      <div className="flex justify-between items-end gap-4 flex-wrap">
        <div>
          <Eyebrow>HostMarket</Eyebrow>
          <H2 size={34}>I professionisti appena arrivati</H2>
        </div>
        <div className="flex gap-2">
          <Link to="/servizi/hostmarket" className="h-11 px-4 rounded-[12px] flex items-center font-bold" style={{ border: "1px solid #111318", color: "#111318" }}>Cerca un professionista</Link>
          <a href="https://hostmarket.vitadahost.it/" target="_blank" rel="noopener noreferrer" className="h-11 px-4 rounded-[12px] flex items-center font-bold" style={{ background: "#111318", color: "#FFFFFF" }}>Proponiti</a>
        </div>
      </div>
      <div ref={scroller} className="flex overflow-x-auto pb-2 gap-4" style={{ scrollbarWidth: "none" }}>
        {items.map((it, i) => (
          <a key={i} href={it.link} target="_blank" rel="noopener noreferrer"
            className="flex-none w-[250px] rounded-[18px] overflow-hidden flex flex-col" style={{ background: "#FFFFFF", border: "1px solid #E4E6EA", scrollSnapAlign: "start" }}>
            <div className="h-[130px] relative" style={{ background: "#EEF0F3" }}>
              {it.photo && <img src={it.photo} alt={it.title || it.name || ""} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />}
            </div>
            <div className="p-[18px] flex flex-col gap-2 flex-1">
              {it.category && <span className="self-start text-[12px] font-extrabold px-2 py-1 rounded-md" style={{ background: "#EEF0F3", color: "#111318" }}>{it.category}</span>}
              <strong className="text-[16px]" style={{ lineHeight: 1.3, color: "#111318" }}>{it.title || it.name}</strong>
              <span className="mt-auto text-[13px]" style={{ color: "#4A505B" }}>{it.city || "Online"}</span>
            </div>
          </a>
        ))}
      </div>
    </section>
  );
}

// ─────────────────────────── 13. CHI C'È DIETRO ───────────────────────────
function ChiDietro() {
  return (
    <section className="flex flex-col gap-5 rounded-[24px] p-6 md:p-8" style={{ background: "#FFFFFF", border: "1px solid #E4E6EA" }} data-testid="home-chi-dietro">
      <H2 size={34}>Chi c'è dietro Vita da Host</H2>
      <div className="flex gap-6 flex-wrap items-center">
        <img src={FREE_CALLS[0].photo} alt="Sergio Battelli, fondatore di Vita da Host" width={120} height={120} loading="lazy" className="w-[120px] h-[120px] rounded-[24px] object-cover flex-none" style={{ background: "#E9EBEF" }} />
        <div className="flex flex-col gap-2" style={{ flex: "1 1 400px" }}>
          <strong className="text-[22px]" style={{ fontFamily: BRIC, color: "#111318" }}>Sergio Battelli</strong>
          <p className="m-0 text-[16px]" style={{ lineHeight: 1.6, color: "#2C313A" }}>
            Host a Genova e fondatore di Vita da Host. Ha creato <a href="/hostland" className="font-bold underline" style={{ color: "#111318" }}>Hostland</a>, il guestbook e welcome book digitale con sito e prenotazioni dirette, ed è autore di <Link to="/servizi/faqbnb" className="font-bold underline" style={{ color: "#111318" }}>FaqBnb</Link>, il manuale pratico per chi gestisce affitti brevi.
          </p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
        {[FREE_CALLS[1], FREE_CALLS[2]].map((e, i) => (
          <div key={e.key} className="flex gap-3.5 items-center rounded-[16px] p-3.5" style={{ background: "#F6F7F9" }}>
            <img src={e.photo} alt={`${e.name}, ${i === 0 ? "home stager e responsabile HostLab" : "commercialista esperto di affitti brevi"}`} width={56} height={56} loading="lazy" className="w-14 h-14 rounded-2xl object-cover flex-none" style={{ background: "#E1E4E9" }} />
            <span>
              <strong style={{ color: "#111318" }}>{e.name}</strong><br />
              <span className="text-[14px]" style={{ color: "#4A505B" }}>{i === 0 ? "Home stager, responsabile HostLab" : "Commercialista esperto di affitti brevi"}</span>
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}

// ─────────────────────────── 14. FAQ ───────────────────────────
function Faq() {
  return (
    <section className="flex flex-col gap-3.5" data-testid="home-faq">
      <H2 size={34}>Domande frequenti su affitti brevi e strutture extralberghiere</H2>
      {HOME_FAQ.map((f, i) => (
        <details key={f.q} className="rounded-[16px] p-[18px]" style={{ background: "#FFFFFF", border: "1px solid #E4E6EA" }} data-testid={`home-faq-${i}`}>
          <summary className="flex justify-between gap-3 font-bold text-[17px] cursor-pointer" style={{ listStyle: "none", color: "#111318" }}>
            {f.q} <span aria-hidden="true">+</span>
          </summary>
          <p className="mt-2.5 mb-0 text-[15px]" style={{ lineHeight: 1.6, color: "#3D434E" }}>
            {f.a}{" "}
            <Link to={f.to} className="font-bold underline" style={{ color: "#111318" }} data-testid={`home-faq-${i}-link`}>
              {f.linkLabel} →
            </Link>
          </p>
        </details>
      ))}
    </section>
  );
}

// ─────────────────────────── 15. INSTAGRAM ───────────────────────────
function Instagram() {
  const [posts, setPosts] = useState([]);
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/instagram/feed?limit=20`).then((r) => { if (alive) setPosts((r.data?.items || []).slice(0, 6)); }).catch(() => {});
    return () => { alive = false; };
  }, []);
  return (
    <section className="flex flex-col gap-4" data-testid="home-instagram">
      <div className="flex justify-between items-center">
        <H2 size={28}>Gli ultimi post da Instagram</H2>
        <a href="https://www.instagram.com/vita_da_host_/" target="_blank" rel="noopener noreferrer" className="font-bold" style={{ color: "#111318" }}>@vita_da_host_ · Seguici →</a>
      </div>
      <div className="grid grid-cols-3 md:grid-cols-6 gap-2.5">
        {(posts.length ? posts : Array.from({ length: 6 })).map((p, i) => (
          <a key={i} href={p?.permalink || "https://www.instagram.com/vita_da_host_/"} target="_blank" rel="noopener noreferrer"
            aria-label="Post Instagram" className="block rounded-[14px] overflow-hidden" style={{ aspectRatio: "1", background: CARD_BG[i % CARD_BG.length] }}>
            {p?.image_url && <img src={p.image_url} alt="" loading="lazy" className="w-full h-full object-cover" />}
          </a>
        ))}
      </div>
    </section>
  );
}

// ─────────────────────────── EXPORT: MAIN COLUMN ───────────────────────────
export default function HomeMain({ reviewsGoogle, reviewsHostLab }) {
  const posts = useBlogFeed(11);
  const featured = posts[0];
  const more = posts.slice(1, 11);
  return (
    <main className="flex flex-col gap-12 md:gap-14 min-w-0" style={{ flex: "999 1 640px" }} data-testid="home-main-column">
      <section className="flex flex-col gap-5" data-testid="home-blog-editorial">
        <div className="flex justify-between items-end gap-4 flex-wrap">
          <H2 size={34}>Ultime notizie su affitti brevi, B&amp;B e case vacanza</H2>
          <Link to="/blog" className="h-11 px-4 rounded-[12px] flex items-center font-bold" style={{ border: "1px solid #111318", color: "#111318" }}>Tutti gli articoli</Link>
        </div>
        <PodcastBar />
        {featured && <FeaturedPost p={featured} />}
      </section>
      <SponsorFull />
      {more.length > 0 && <BlogMore items={more} />}
      {reviewsGoogle}
      {reviewsHostLab}
      <CosE />
      <Faq />
    </main>
  );
}
